import { Divider } from 'antd';
import React, { useEffect, useState } from 'react';
import { DocumentUploader } from '../../../components/DocumentUploader';
import Button from '../../../components/button/Button';
import { useUserProfile } from '../../../customhooks/useUserProfile';
import { EditIcon, FileIcon } from '../../../components/icons';
import BusinessDocumentViewer from './BusinessDocumentViewer';
import { getDocumentNameAndExtension } from './ProfileDocuments';
import { mutateConfig } from '../../../api/queryBase';

const CompanyLogo = () => {
    const [companyLogoUrl, setCompanyLogoUrl] = useState<string>('');
    const [newCompanyLogoUrl, setNewCompanyLogoUrl] = useState<string | null>(
        null
    );
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const { userDetails } = useUserProfile();

    useEffect(() => {
        if (userDetails?.corporate?.companyLogoUri) {
            setCompanyLogoUrl(userDetails.corporate.companyLogoUri);
            setIsEditing(false);
        } else {
            setIsEditing(true);
        }
    }, [userDetails]);

    const mutation = mutateConfig({ method: 'put' });
    const { mutate, isLoading } = mutation({
        url: `/corporates/update`,
        cacheKeys: 'users-profile'
    });

    const handleUpdate = () => {
        if (!newCompanyLogoUrl) return;
        mutate(
            { companyLogoUri: newCompanyLogoUrl },
            {
                onSuccess: () => {
                    setCompanyLogoUrl(newCompanyLogoUrl);
                    setNewCompanyLogoUrl(null);
                    setIsEditing(false);
                }
            }
        );
    };

    return (
        <div className='flex flex-col lg:max-w-[500px] max-w-[100%] w-full mb-6 mx-auto lg:mx-0 mt-2 gap-[5px]'>
            <div className='flex justify-between items-center'>
                <h3 className='text-deepMidnightBlue text-[16px] font-[500]'>
                    Company Logo
                </h3>

                {!isEditing && companyLogoUrl && (
                    <div
                        className='flex items-center justify-center rounded-[8px] p-[12px] cursor-pointer hover:bg-gray-100'
                        onClick={() => setIsEditing(true)}
                    >
                        <EditIcon />
                    </div>
                )}
            </div>

            <Divider className='mt-2' />

            {!isEditing ? (
                <div className='min-h-[72px] p-3 flex items-center justify-between border border-primary500 bg-aliceBlue rounded-xl'>
                    <div className='flex w-full gap-2 items-center justify-between pr-2'>
                        <div className='flex items-center'>
                            <FileIcon />
                            <div className='pl-4'>
                                <p className='text-[#344054] text-[14px] font-[500]'>
                                    {
                                        getDocumentNameAndExtension(
                                            companyLogoUrl
                                        ).name
                                    }
                                </p>
                            </div>
                        </div>
                        <BusinessDocumentViewer
                            useApi={false}
                            url={companyLogoUrl}
                            ext={
                                companyLogoUrl?.split('?size=')[0]?.split('.')[
                                    companyLogoUrl
                                        ?.split('?size=')[0]
                                        ?.split('.').length - 1
                                ]
                            }
                        />
                    </div>
                </div>
            ) : (
                <DocumentUploader
                    useAuthorization
                    key='companyLogo'
                    title='Upload Company Logo'
                    runAfterUpload
                    accept='image/png,image/jpg,image/jpeg'
                    acceptText='PNG or JPG (max. 800x400px)'
                    documentItemId={1}
                    uploaded={!!newCompanyLogoUrl}
                    documentUrl={newCompanyLogoUrl || ''}
                    status={2}
                    onUpload={(url: string) => {
                        setNewCompanyLogoUrl(url);
                    }}
                />
            )}

            {isEditing && (
                <div className='flex gap-4'>
                    <Button
                        type='button'
                        variant='secondaryOutline'
                        text='Cancel'
                        className='flex-1'
                        onClick={() => {
                            setNewCompanyLogoUrl(null);
                            setIsEditing(false);
                        }}
                        disabled={!companyLogoUrl}
                    />
                    <Button
                        type='button'
                        isLoading={isLoading}
                        text='Update'
                        className='flex-1'
                        onClick={handleUpdate}
                        disabled={!newCompanyLogoUrl}
                    />
                </div>
            )}
        </div>
    );
};

export default CompanyLogo;
